import type { SelectableFilterColumn } from "@/dashboard/inventory_management/common/portfolioTableColumns";
import {
  NONE_FILTER_VALUE,
  type FilterOption,
} from "@/dashboard/inventory_management/common/portfolioTableFilters";
import { FlattenedPortfolioItem } from "@/types";
import type { TranslationFn } from "@/utils/countries";
import { AUTHORITY_FLAGS } from "@/utils/registryAccounts";

/** List columns whose options are the distinct string values present in the items. */
type StringListColumn =
  | Exclude<
      SelectableFilterColumn,
      "production_support" | "ecolabel_tags" | "technology_type"
    >
  | "limit_price"
  | "sale_price"
  | "account_emails";

interface StringFilterOption {
  label: string;
  value: string;
}

/** Each list column's filter-editor options, which the chips and URL restore also read. */
export type FilterOptionsByColumn = Record<
  StringListColumn,
  StringFilterOption[]
> & {
  /** Ends with an Unknown (`null`) option when some item has no technology. */
  technology_type: FilterOption[];
  production_support: FilterOption[];
  ecolabel_tags: FilterOption[];
};

/**
 * Display labels the table already computes for its cells, keyed by raw value. A value missing from
 * its map is shown as itself.
 */
export interface FilterOptionLabels {
  technology: ReadonlyMap<string, string>;
  status: ReadonlyMap<string, string>;
  transferDeadline: ReadonlyMap<string, string>;
  /** Shared by registry_region and issuing_region. */
  registry: ReadonlyMap<string, string>;
}

type ValuePicker<V> = (item: FlattenedPortfolioItem) => V | null | undefined;

/**
 * Builds every list column's filter options from the items: each column's distinct non-empty values,
 * sorted (prices numerically, everything else lexicographically) and labelled the way the cells show
 * them. A price column leads with a "-" option only when some item has no price; technology ends
 * with an Unknown option only when some item has none.
 */
export const buildFilterOptions = (
  items: readonly FlattenedPortfolioItem[],
  labels: FilterOptionLabels,
  t: TranslationFn
): FilterOptionsByColumn => {
  const distinct = <V extends string>(
    pick: (item: FlattenedPortfolioItem) => readonly (V | null | undefined)[]
  ): V[] => {
    const values = new Set<V>();
    for (const item of items) {
      for (const value of pick(item)) {
        if (value) values.add(value);
      }
    }
    return [...values];
  };
  const sorted = <V extends string>(pick: ValuePicker<V>): V[] =>
    distinct((item) => [pick(item)]).sort();
  const labelled = <V extends string>(
    values: V[],
    label: (value: V) => string = (value) => value
  ): StringFilterOption[] =>
    values.map((value) => ({ label: label(value), value }));
  const fromMap = (map: ReadonlyMap<string, string>) => (value: string) =>
    map.get(value) ?? value;
  const priceOptions = (
    pick: ValuePicker<string>,
    label: (value: string) => string
  ): StringFilterOption[] => [
    ...(items.some((item) => pick(item) == null)
      ? [{ label: "-", value: NONE_FILTER_VALUE }]
      : []),
    ...labelled(
      distinct((item) => [pick(item)]).sort(
        (a, b) => parseFloat(a) - parseFloat(b)
      ),
      label
    ),
  ];

  return {
    // The overview's technology breakdown filters to rows without a technology, so that value needs
    // an option for its chip label, its header editor and its URL restore.
    technology_type: [
      ...labelled(
        sorted((item) => item.technology_type),
        fromMap(labels.technology)
      ),
      ...(items.some((item) => item.technology_type == null)
        ? [{ label: t("common.unknown"), value: null }]
        : []),
    ],
    status: labelled(
      sorted((item) => item.status),
      fromMap(labels.status)
    ),
    portfolio_name: labelled(sorted((item) => item.portfolio_name)),
    portfolio_type: labelled(
      sorted((item) => item.portfolio_type),
      (value) => t(`charts.values.portfolio_type.${value}`, value)
    ),
    production_device_name: labelled(
      sorted((item) => item.production_device_name)
    ),
    transfer_deadline: labelled(
      sorted((item) => item.transfer_deadline),
      fromMap(labels.transferDeadline)
    ),
    production_month: labelled(sorted((item) => item.production_month)),
    registration_authority: labelled(
      sorted((item) => item.registration_authority),
      (value) => `${AUTHORITY_FLAGS[value]} ${value}`
    ),
    registry_region: labelled(
      sorted((item) => item.registry_region),
      fromMap(labels.registry)
    ),
    issuing_region: labelled(
      sorted((item) => item.issuing_region),
      fromMap(labels.registry)
    ),
    limit_price: priceOptions(
      (item) => item.limit_price,
      (value) => value
    ),
    sale_price: priceOptions(
      (item) => item.sale_price,
      (value) => `${value} €`
    ),
    account_emails: labelled(
      distinct((item) => item.account_emails ?? []).sort()
    ),
    production_support: [
      { label: t("common.yes"), value: true },
      { label: t("common.no"), value: false },
      { label: t("common.unknown"), value: null },
    ],
    ecolabel_tags: [
      { label: "ekoenergy", value: "ekoenergy" },
      { label: "okpower-new", value: "okpower-new" },
      { label: t("common.none"), value: NONE_FILTER_VALUE },
    ],
  };
};
